import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Calendar,
    Clock,
    MapPin,
    Globe,
    ShieldCheck,
    CheckCircle2,
    AlertCircle,
    XCircle,
    PackageOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/screens/_shared";
import {
    getBookingsByBuyer,
    getBookingsBySeller,
    subscribeBookings,
} from "@/lib/bookingStore";

const fmt = (n) => "₦" + Number(n || 0).toLocaleString();

const STATUS_META = {
    pending:   { label: "Pending",   tone: "amber",   icon: Clock },
    completed: { label: "Completed", tone: "emerald", icon: CheckCircle2 },
    released:  { label: "Released",  tone: "emerald", icon: CheckCircle2 },
    disputed:  { label: "Disputed",  tone: "red",     icon: AlertCircle },
    cancelled: { label: "Cancelled", tone: "muted",   icon: XCircle },
};

const useBookingsVersion = () => {
    const [v, setV] = useState(0);
    useEffect(() => subscribeBookings(() => setV((x) => x + 1)), []);
    return v;
};

const Bookings = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useBookingsVersion();
    const [tab, setTab] = useState("buyer"); // buyer | seller

    const buyerList = useMemo(
        () => (user ? getBookingsByBuyer(user.id) : []),
        [user, v]
    );
    const sellerList = useMemo(
        () => (user ? getBookingsBySeller(user.id) : []),
        [user, v]
    );

    const list = tab === "buyer" ? buyerList : sellerList;

    return (
        <div className="screen" data-testid="bookings-screen">
            <PageHeader title="Bookings" />
            <div className="px-5 pt-3">
                <div className="flex gap-2" data-testid="bookings-tabs">
                    <TabBtn
                        testId="bookings-tab-buyer"
                        active={tab === "buyer"}
                        onClick={() => setTab("buyer")}
                        label="My bookings"
                        count={buyerList.length}
                    />
                    <TabBtn
                        testId="bookings-tab-seller"
                        active={tab === "seller"}
                        onClick={() => setTab("seller")}
                        label="Received"
                        count={sellerList.length}
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4">
                {list.length === 0 ? (
                    <div className="py-16 text-center" data-testid="bookings-empty">
                        <PackageOpen size={28} className="mx-auto text-muted-foreground/60" />
                        <p className="mt-2 text-[13px] font-medium">
                            {tab === "buyer" ? "No bookings yet" : "No bookings received yet"}
                        </p>
                        <p className="mt-1 text-[11.5px] text-muted-foreground max-w-[30ch] mx-auto">
                            {tab === "buyer"
                                ? "When you book a service, it will appear here so you can track it."
                                : "Once customers book your services, you'll see them here."}
                        </p>
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {list.map((b) => (
                            <BookingRow
                                key={b.id}
                                booking={b}
                                role={tab}
                                onClick={() => navigate(`/bookings/${b.id}`)}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

const TabBtn = ({ active, onClick, label, count, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className={[
            "h-10 px-4 rounded-xl text-[12.5px] font-medium inline-flex items-center gap-1.5 border transition-colors",
            active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-secondary text-foreground border-border hover:bg-muted",
        ].join(" ")}
    >
        {label}
        <span
            className={[
                "min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center",
                active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground",
            ].join(" ")}
        >
            {count}
        </span>
    </button>
);

const BookingRow = ({ booking, role, onClick }) => {
    const meta = STATUS_META[booking.status] || STATUS_META.pending;
    const Icon = meta.icon;
    const toneClass = {
        amber:   "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
        emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
        red:     "bg-destructive/10 text-destructive border-destructive/30",
        muted:   "bg-muted text-muted-foreground border-border",
    }[meta.tone];
    return (
        <li>
            <button
                type="button"
                data-testid={`booking-row-${booking.id}`}
                onClick={onClick}
                className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border bg-card hover:bg-accent/30 active:scale-[0.99] transition-[transform,background-color] text-left"
            >
                {booking.serviceImage ? (
                    <img
                        src={booking.serviceImage}
                        alt=""
                        className="h-14 w-14 rounded-xl object-cover shrink-0"
                    />
                ) : (
                    <div className="h-14 w-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Calendar size={18} />
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="font-display text-[14px] font-semibold truncate">
                        {booking.serviceName}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground truncate inline-flex items-center gap-1.5">
                        <Calendar size={11} />
                        {booking.date} · {booking.time}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground truncate">
                        {role === "buyer"
                            ? `Provider: ${booking.sellerName}`
                            : `Customer: ${booking.buyerName}`}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                    <span
                        className={[
                            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold border",
                            toneClass,
                        ].join(" ")}
                    >
                        <Icon size={10} />
                        {meta.label}
                    </span>
                    <span className="font-display text-[13px] font-semibold">
                        {fmt(booking.amount)}
                    </span>
                </div>
            </button>
        </li>
    );
};

export default Bookings;
